import { 
  ApplicationConfig, 
  inject, 
  provideAppInitializer, 
  provideZoneChangeDetection,
  ChangeDetectorRef // 👈 Importamos el ChangeDetectorRef público y estándar
} from '@angular/core';
import { NavigationStart, provideRouter, RouteReuseStrategy, Router, withComponentInputBinding } from '@angular/router';
import { ListRouteReuseStrategy } from './core/routing/list-route-reuse.strategy';
import { ListNavigationContextService } from './core/routing/list-navigation-context.service';
import { provideHttpClient, HttpClient, withInterceptors, withFetch } from '@angular/common/http';
import {
  provideTranslateService,
  TranslateLoader,
  TranslateService,
  type TranslationObject,
} from '@ngx-translate/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideNativeDateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { SessionExpiryWatcherService } from './core/services/session-expiry-watcher.service';
import { forkJoin, map, Observable, firstValueFrom, catchError, of } from 'rxjs';

// Importamos los dos componentes de la librería para repararlos globalmente
import { ToolbarComponent, FilterComponent } from '@tecalisdev/infinity';

/**
 * 1. Cargador Multi-fichero: Combina JSONs de la App y de la Librería
 */
class MultiHttpLoader implements TranslateLoader {
  constructor(private http: HttpClient, public resources: string[]) {}

  getTranslation(lang: string): Observable<TranslationObject> {
    const requests = this.resources.map((path) => 
      this.http.get(`${path}${lang}.json`).pipe(
        catchError((err) => {
          console.error(`Error cargando traducción en: ${path}${lang}.json`, err);
          return of({}); // Si falla un fichero, devolvemos objeto vacío para no romper el forkJoin
        })
      )
    );
    return forkJoin(requests).pipe(
      map((responses) => Object.assign({}, ...responses) as TranslationObject),
    );
  }
}

/**
 * 2. Factory: Asegúrate de que estas rutas existan en tu carpeta /public o /assets
 */
const httpLoaderFactory = (http: HttpClient) => 
  new MultiHttpLoader(http, [
    '/assets/i18n/infinity/', // Traducciones de la librería Tecalis
    '/assets/i18n/'           // Traducciones de tu aplicación
  ]);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withFetch(), withInterceptors([authInterceptor])),
    // 3. Configuración de Traducción
    provideTranslateService({
      defaultLanguage: 'es',
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient],
      },
    }),

    // 4. Inicializador: Bloquea el renderizado hasta que el JSON esté listo
    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      return firstValueFrom(translate.use('es')).catch(err => {
        console.error('Fallo crítico cargando traducciones iniciales', err);
      });
    }),

    provideAppInitializer(() => {
      const router = inject(Router);
      const navigationContext = inject(ListNavigationContextService);
      router.events.subscribe((event) => {
        if (event instanceof NavigationStart) {
          const nav = router.getCurrentNavigation();
          navigationContext.capture(
            event.navigationTrigger ?? nav?.trigger ?? 'imperative',
            nav?.extras?.state ?? null,
          );
        }
      });
    }),

    provideAppInitializer(() => {
      inject(SessionExpiryWatcherService).start();
    }),

    // 🔥 6. PARCHE GLOBAL SEGURO: Inyección de CDR mediante Factorías de Angular Ivy
    provideAppInitializer(() => {
      
      const originalToolbarFactory = (ToolbarComponent as any).ɵfac;
      if (originalToolbarFactory) {
        (ToolbarComponent as any).ɵfac = function (t: any) {
          const instance = originalToolbarFactory(t);
          // Al estar dentro de la factoría de Angular, estamos en pleno Injection Context.
          // Inyectamos el ChangeDetectorRef públicamente y lo guardamos en la instancia.
          instance.__customCdr = inject(ChangeDetectorRef);
          return instance;
        };
      }

      const originalFilterFactory = (FilterComponent as any).ɵfac;
      if (originalFilterFactory) {
        (FilterComponent as any).ɵfac = function (t: any) {
          const instance = originalFilterFactory(t);
          instance.__customCdr = inject(ChangeDetectorRef);
          return instance;
        };
      }

      // === PASO B: Modificamos los prototipos usando el ChangeDetectorRef inyectado ===

      // 1. Apertura desde el Toolbar
      const originalOpen = ToolbarComponent.prototype.openFilter;
      ToolbarComponent.prototype.openFilter = function (this: any) {
        originalOpen.call(this); // Cambia filterIsOpen = true
        // Forzamos al Toolbar a renderizarse inmediatamente para que le pase el nuevo valor [isOpen] al Filtro hijo
        this.__customCdr?.detectChanges();
      };

      // 2. Cierre desde el Toolbar (Tarda 500ms por su setTimeout nativo)
      const originalToolbarClose = ToolbarComponent.prototype.closeFilter;
      ToolbarComponent.prototype.closeFilter = function (this: any) {
        originalToolbarClose.call(this);
        setTimeout(() => {
          this.__customCdr?.detectChanges();
        }, 520);
      };

      // 3. Captura de la apertura interna del Filtro mediante sus cambios de propiedades
      const originalNgOnChanges = FilterComponent.prototype.ngOnChanges;
      FilterComponent.prototype.ngOnChanges = function (this: any, changes: any) {
        originalNgOnChanges.call(this, changes);
        
        // Si el Toolbar le notificó que debe abrirse, esperamos a que pase su setTimeout de 10ms
        // interno y obligamos al Filtro a repintar sus estados '_isOpen' y 'showBackdrop'
        if (changes['isOpen'] && changes['isOpen'].currentValue === true) {
          setTimeout(() => {
            this.__customCdr?.detectChanges();
          }, 25);
        }
      };

      // 4. Cierre del Filtro (Tarda 250ms por su backdrop nativo)
      const originalFilterClose = FilterComponent.prototype.closeFilter;
      FilterComponent.prototype.closeFilter = function (this: any) {
        originalFilterClose.call(this);
        setTimeout(() => {
          this.__customCdr?.detectChanges();
        }, 270);
      };

    }),

    provideAnimationsAsync(),
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
    { provide: RouteReuseStrategy, useClass: ListRouteReuseStrategy },
  ],
};